/**
 * The guide pages' palette, mapped onto the site's design tokens.
 *
 * The thirteen guide pages each carried a private copy of the same terracotta
 * palette: a warm cream, an espresso text colour, and #C4704B terracotta as
 * the action colour. It was consistent with itself and with nothing else. None
 * of those values appear in index.css, so a visitor going from the directory
 * to a guide crossed from navy and green into orange with no header or footer
 * to tell them they were still on the same site.
 *
 * Rather than rewrite eight thousand lines of markup, this keeps every colour
 * NAME the pages already use and changes only what each one resolves to.
 *
 * WHY THESE ARE LITERAL VALUES AND NOT var(--primary)
 *
 * Eight of the thirteen guides are a complete HTML document written into an
 * iframe with document.write. An iframe document does not inherit the parent's
 * custom properties, so `var(--primary)` inside one resolves to nothing and
 * the rule is dropped. The two families would then diverge again, which is the
 * problem this file exists to end.
 *
 * So the values are resolved oklch literals, which work identically in the
 * main document and inside an iframe. guideTheme.test.ts asserts each one
 * still matches the token it was copied from in index.css, so retuning a token
 * fails the build here rather than drifting silently.
 *
 * The mapping, and why:
 *
 *   terracotta      -> primary (navy). The action colour. This is the
 *                      substantive change: buttons and links on a guide now
 *                      match buttons and links everywhere else.
 *   sage / green    -> the Create role green, which these pages were already
 *                      reaching for with three different hand-mixed greens.
 *   cream / linen   -> background / muted. Both were warm off-whites, and the
 *                      site has warm off-whites.
 *   espresso / body -> foreground / muted-foreground.
 */

/** Mirrors index.css. Kept honest by guideTheme.test.ts. */
export const SITE_TOKENS = {
  background: "oklch(0.98 0.008 85)",
  foreground: "oklch(0.22 0.015 65)",
  card: "oklch(1 0.004 85)",
  primary: "oklch(0.32 0.08 255)",
  muted: "oklch(0.93 0.01 85)",
  mutedForeground: "oklch(0.52 0.015 65)",
  border: "oklch(0.88 0.012 85)",
} as const;

/** The Create role green, from roleDisplay. Not a :root token, but load-bearing. */
const CREATE_GREEN = "oklch(0.38 0.09 155)";

/** A darker navy for hover and active states, formerly the hand-mixed #A05A38. */
const PRIMARY_DARK = "oklch(0.26 0.075 255)";

/**
 * Keyed by the names the pages already use.
 *
 * The two spellings are deliberate: the iframe pages declare
 * `--terracotta-light` in CSS, the React pages use `terracottaLight` in an
 * inline-style object. Both are here so neither family needs its markup
 * restructured.
 */
export const GUIDE_COLORS = {
  // Surfaces
  cream: SITE_TOKENS.background,
  linen: SITE_TOKENS.muted,
  white: SITE_TOKENS.card,
  border: SITE_TOKENS.border,

  // Text
  espresso: SITE_TOKENS.foreground,
  body: SITE_TOKENS.mutedForeground,
  muted: SITE_TOKENS.mutedForeground,

  // The action colour, formerly #C4704B.
  terracotta: SITE_TOKENS.primary,
  terracottaDark: PRIMARY_DARK,
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",

  // Success and "do this" accents.
  sage: CREATE_GREEN,
  sageLight: "oklch(0.38 0.09 155 / 0.10)",
  green: CREATE_GREEN,
} as const;

export type GuideColorName = keyof typeof GUIDE_COLORS;

/**
 * The palette as CSS custom properties, for the iframe guides, which reference
 * `var(--terracotta)` throughout their markup.
 *
 * Emitted at :root because inside the iframe there is nothing else in the
 * document to collide with. In the main document this would be unsafe, since
 * --muted and --border are site tokens and redefining them at :root would
 * repaint SiteHeader from inside a page.
 */
export const GUIDE_CSS_VARS = `
  :root {
    --cream: ${GUIDE_COLORS.cream};
    --linen: ${GUIDE_COLORS.linen};
    --white: ${GUIDE_COLORS.white};
    --border: ${GUIDE_COLORS.border};
    --espresso: ${GUIDE_COLORS.espresso};
    --body: ${GUIDE_COLORS.body};
    --muted: ${GUIDE_COLORS.muted};
    --terracotta: ${GUIDE_COLORS.terracotta};
    --terracotta-light: ${GUIDE_COLORS.terracottaLight};
    --terracotta-mid: ${GUIDE_COLORS.terracottaMid};
    --sage: ${GUIDE_COLORS.sage};
    --sage-light: ${GUIDE_COLORS.sageLight};
  }
`;

/**
 * The stylesheet link the iframe documents were missing entirely.
 *
 * They set font-family to 'Fraunces' and 'Plus Jakarta Sans' but never loaded
 * either one, and an iframe does not inherit the parent document's @font-face
 * rules. Every one of these guides has therefore been rendering in the
 * browser's default serif and sans the whole time, which is a good part of why
 * they read as a different site even before the colours are considered.
 *
 * Same href as client/index.html.
 */
export const GUIDE_FONT_LINK = `
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
`;

/**
 * Literal hex values that were scattered through the markup outside the
 * palette declarations, mapped to their replacements.
 *
 * These are the ones a palette swap alone would miss: a page declares
 * --terracotta and then, forty lines later, hardcodes #A05A38 for a hover
 * state. #A05A38 appears twenty times across the thirteen files and is
 * declared in none of them.
 *
 * Longest-first ordering does not matter here because every key is a
 * six-digit hex, but matching must be case-insensitive: the files mix #FAF8F5
 * and #faf8f5.
 */
export const STRAY_HEX_REPLACEMENTS: Record<string, string> = {
  // Terracotta and its hand-mixed hover and active variants.
  "#C4704B": GUIDE_COLORS.terracotta,
  "#A05A38": PRIMARY_DARK,
  "#B05A3D": PRIMARY_DARK,
  "#B05E3A": PRIMARY_DARK,
  "#E8C4B0": GUIDE_COLORS.terracottaMid,
  "#F5EAE3": GUIDE_COLORS.terracottaLight,

  // Surfaces.
  "#FAF8F5": GUIDE_COLORS.cream,
  "#F5F0E8": GUIDE_COLORS.cream,
  "#F7F4EF": GUIDE_COLORS.cream,
  "#F0EDE8": GUIDE_COLORS.linen,
  "#EDE6DC": GUIDE_COLORS.linen,
  "#FFFFFF": GUIDE_COLORS.white,

  // Borders.
  "#E8E2DA": GUIDE_COLORS.border,
  "#E0D8CC": GUIDE_COLORS.border,
  "#B0A89F": GUIDE_COLORS.border,
  "#B0A89E": GUIDE_COLORS.border,

  // Text.
  "#2D2926": GUIDE_COLORS.espresso,
  "#1A1008": GUIDE_COLORS.espresso,
  "#5A4F48": GUIDE_COLORS.body,
  "#5A4A3A": GUIDE_COLORS.body,
  "#7A6F68": GUIDE_COLORS.muted,
  "#9A8F88": GUIDE_COLORS.muted,

  // Greens, three hand-mixed variants of one idea.
  "#3D5A47": CREATE_GREEN,
  "#2A6B5A": CREATE_GREEN,
  "#2A5A3A": CREATE_GREEN,
  "#1A3A2A": "oklch(0.30 0.08 155)",
  "#EBF0EC": GUIDE_COLORS.sageLight,
};
